import classNames from "classnames";
import { Immutable } from "immer";
import React, {
    CSSProperties,
    type JSX,
    memo,
    useCallback,
    useEffect,
    useLayoutEffect,
    useRef,
    useState,
} from "react";
import SVG from "react-inlinesvg";
import {
    Modal,
    ModalStyles,
} from "tsi-common-react/src/components/Modal/index";
import { PromoAd } from "tsi-common-react/src/components/PromoAd/index";
import { BreakPoint } from "tsi-common-react/src/models/screen";
import {
    getViewportBreakpoint,
    isMobile,
} from "tsi-common-react/src/utils/detectMobile";
import { focusElement } from "tsi-common-react/src/utils/keyboardFocus";
import { useIsSSR } from "tsi-common-react/src/utils/ssr";
import { t } from "ttag";

import { getDocument, getWindow } from "@thelabnyc/thelabui/src/utils/dom";

import iconBars from "../../assets/svg/bars.svg";
import iconCaretDown from "../../assets/svg/caret-down.svg";
import { CloseButton } from "../CloseButton";
import { CategoryLink } from "./CategoryLink";
import { DropdownLinks } from "./DropdownLinks";
import { MenuAdverts } from "./MenuAdverts";
import { NavFooter } from "./NavFooter";
import { PageLink } from "./PageLink";
import {
    IMenuCategoryLink,
    IMenuItem,
    IMenuPageLinkWithImage,
    IMenuSnippetLink,
} from "./menu.interface";

type IProps = Immutable<{
    menuItems: IMenuItem[];
    dropdownOffsetTop?: number;
}>;

type IMenuList = Immutable<{
    menuItems: IMenuItem[];
    isMobile: boolean;
    openedLink: string;
}> & {
    onOpenModal: (
        event?: React.MouseEvent<HTMLElement>,
        subCategory?: Immutable<IMenuCategoryLink>,
    ) => void;
};

// Memoized sub-components
const MobileTriggerButton = memo(
    ({ onOpenModal }: { onOpenModal: () => void }) => (
        <button
            tabIndex={0}
            className="mobile-toggle"
            aria-label={t`Open Menu`}
            title={t`Open Menu`}
            onClick={onOpenModal}
        >
            <SVG
                className="site-nav__mobile-toggle-icon"
                src={iconBars}
                title={t`Menu Icon`}
                aria-hidden="true"
            />
        </button>
    ),
);

const BackButton = memo(({ onBack }: { onBack: () => void }) => (
    <button
        title={t`Back`}
        className="main-dropdown-menu__back"
        onClick={onBack}
    >
        <SVG
            className="main-dropdown-menu__back-icon"
            src={iconCaretDown}
            title={t`Dropdown Menu Icon`}
            aria-hidden="true"
        />
        {t`Back`}
    </button>
));

const MenuList = memo(
    ({ menuItems, isMobile, onOpenModal, openedLink }: IMenuList) => {
        let index = 0;
        const menuLinks = menuItems.reduce<JSX.Element[]>((memo, menuItem) => {
            switch (menuItem.type) {
                case "nav_category":
                    const link = (
                        <li key={menuItem.value.title}>
                            <CategoryLink
                                linkObject={menuItem}
                                isMobile={isMobile}
                                linkID={index++}
                                openModal={onOpenModal}
                                openedLink={openedLink}
                            />
                        </li>
                    );
                    return memo.concat(link);
                case "page_link":
                    return memo.concat(
                        <li key={menuItem.value.override_title}>
                            <PageLink
                                linkObject={menuItem}
                                isMobile={isMobile}
                                linkID={index++}
                            />
                        </li>,
                    );
                case "nav_advert":
                    if (isMobile) {
                        return memo.concat(
                            <li key={`advert-${index++}`}>
                                <div className="main-dropdown-menu__adverts">
                                    <PromoAd promoArrayEl={menuItem} />
                                </div>
                            </li>,
                        );
                    }
                    break;
            }
            return memo;
        }, []);

        return <ul className="main-menu-list">{menuLinks}</ul>;
    },
);

const DropdownContent = memo(
    ({
        activeMenuItem,
        isMobile,
        hasAdverts = false,
    }: {
        activeMenuItem: Immutable<IMenuCategoryLink> | null;
        isMobile: boolean;
        hasAdverts?: boolean;
    }) => {
        if (!activeMenuItem) return null;
        return (
            <DropdownLinks
                isActive={true}
                isMobile={isMobile}
                hasAdverts={hasAdverts}
                linksArray={activeMenuItem.value.sub_nav.filter(
                    (val): val is IMenuPageLinkWithImage | IMenuSnippetLink => {
                        return (
                            val.type === "page_link_with_image" ||
                            val.type === "snippet_link"
                        );
                    },
                )}
                linkID={0}
                key={0}
            />
        );
    },
);

const getPromoAdsAndFooter = (
    activeMenuItem: Immutable<IMenuCategoryLink> | null,
) => {
    const ads: JSX.Element[] = [];
    let footer: JSX.Element | null = null;
    let isDefaultAdAdded = false;

    if (activeMenuItem && activeMenuItem.value.sub_nav.length > 0) {
        activeMenuItem.value.sub_nav.forEach((val, i) => {
            switch (val.type) {
                case "nav_advert":
                    if (val.value?.promo_period?.is_promo_active) {
                        if (isDefaultAdAdded && ads.length > 0) {
                            ads.pop();
                        }
                        ads.push(<PromoAd key={i} promoArrayEl={val} />);
                    } else if (
                        ads.length === 0 &&
                        (val.value?.promo_period === undefined ||
                            val.value?.promo_period === null ||
                            !val.value?.promo_period?.is_promo_active)
                    ) {
                        ads.push(<PromoAd key={i} promoArrayEl={val} />);
                        isDefaultAdAdded = true;
                    }
                    break;
                case "nav_footer":
                    footer = <NavFooter navFooterEl={val} />;
                    break;
            }
        });
    }

    return { promoAds: ads, navFooter: footer };
};

export const MainMenu: React.FC<IProps> = ({
    menuItems,
    dropdownOffsetTop,
}) => {
    const isSSR = useIsSSR();
    // State
    const [isMobileState, setIsMobileState] = useState<boolean | null>(null);
    const [iframeDocHeight, setIframeDocHeight] = useState<number | null>(null);
    const [modalIsOpen, setModalIsOpen] = useState(false);
    const [activeMenuItem, setActiveMenuItem] =
        useState<Immutable<IMenuCategoryLink> | null>(null);
    const [submenuParentElement, setSubmenuParentElement] =
        useState<HTMLElement | null>(null);
    const [openedLink, setOpenedLink] = useState("");

    // Refs
    const iframeHeightCallbackSet = useRef(false);
    const timeoutRefs = useRef<Set<NodeJS.Timeout>>(new Set());
    // Keep refs current so event handlers never read stale closure values
    // without needing to re-register listeners on every state change
    const modalIsOpenRef = useRef(modalIsOpen);
    const isMobileStateRef = useRef(isMobileState);
    const isSSRRef = useRef(isSSR);
    const submenuParentElementRef = useRef(submenuParentElement);
    modalIsOpenRef.current = modalIsOpen;
    isMobileStateRef.current = isMobileState;
    isSSRRef.current = isSSR;
    submenuParentElementRef.current = submenuParentElement;

    // synchronous client viewport detection — runs before first paint to avoid CLS
    useLayoutEffect(() => {
        const initialMobile = getViewportBreakpoint() < BreakPoint.MEDIUM;
        if (initialMobile !== isMobileState) {
            setIsMobileState(initialMobile);
        }
    }, []);

    // Iframe height callback — safe to run after hydration completes
    useEffect(() => {
        if (!isSSRRef.current) {
            setIframeHeightCallback();
        }
    }, []);

    // Replace WindowResizeSensor: useEffect is never called during SSR,
    // registered exactly once, refs keep values current without re-registration
    useEffect(() => {
        const win = getWindow();
        if (!win) return;

        const handleResize = () => {
            // Read fresh breakpoint — not a stale closure value
            const isMobileNow = getViewportBreakpoint() < BreakPoint.MEDIUM;
            // Match sealy/pillows pattern: skip resize handling on Android
            // soft-keyboard viewport changes to prevent unintended modal close
            if (isMobileNow && isMobile.Android()) {
                return;
            }
            if (isMobileNow !== isMobileStateRef.current) {
                setIsMobileState(isMobileNow);
                if (modalIsOpenRef.current) {
                    setModalIsOpen(false);
                    setActiveMenuItem(null);
                    setOpenedLink("");
                }
            }
        };

        win.addEventListener("resize", handleResize);
        return () => {
            win.removeEventListener("resize", handleResize);
        };
        // empty deps: refs keep values current
    }, []);

    // Cleanup timeouts on unmount
    useEffect(() => {
        return () => {
            timeoutRefs.current.forEach(clearTimeout);
            timeoutRefs.current.clear();
        };
    }, []);

    const setIframeHeightCallback = () => {
        if (isSSRRef.current || iframeHeightCallbackSet.current) {
            return;
        }
        const window = getWindow();
        if (!window?.parentIFrame) {
            return;
        }
        window.parentIFrame.getPageInfo((info) => {
            setIframeDocHeight(info.windowHeight);
        });
        iframeHeightCallbackSet.current = true;
    };

    // Safe timeout wrapper — stable reference since it only reads refs
    const setSafeTimeout = useCallback(
        (callback: () => void, delay: number) => {
            const timeoutId = setTimeout(() => {
                callback();
                timeoutRefs.current.delete(timeoutId);
            }, delay);
            timeoutRefs.current.add(timeoutId);
            return timeoutId;
        },
        [],
    );

    // Event handlers — useCallback with refs for stable identity across renders
    const onOpenModal = useCallback(
        (
            event?: React.MouseEvent<HTMLElement>,
            subCategory?: Immutable<IMenuCategoryLink>,
        ) => {
            let menuLinkElem: HTMLElement | null = null;
            if (event) {
                event.preventDefault();
                menuLinkElem = event.currentTarget;
            }
            if (!isSSRRef.current && menuLinkElem) {
                const document = getDocument();
                if (
                    !menuLinkElem.classList.contains(
                        "site-nav__main-menu-item--mobile",
                    )
                ) {
                    document?.body.classList.add("body-dropdown-open--desktop");
                } else {
                    const layout = document?.querySelector(".layout");
                    if (layout) {
                        layout.classList.add("layout--hidden");
                    }
                }
            }
            setModalIsOpen(true);
            setActiveMenuItem(subCategory || null);
            setSubmenuParentElement(menuLinkElem);
            setOpenedLink(subCategory?.value.title || "");

            if (subCategory && !isSSRRef.current) {
                setSafeTimeout(() => {
                    focusElement(".main-dropdown-menu__back");
                }, 300);
            }
        },
        [setSafeTimeout],
    );

    const onCloseModal = useCallback(
        (event?: React.MouseEvent<Element> | React.KeyboardEvent<Element>) => {
            let menuLinkElem: HTMLElement | null = null;
            if (event) {
                event.preventDefault();
                menuLinkElem = event.currentTarget as HTMLElement;
            }

            if (!isSSRRef.current && menuLinkElem) {
                const document = getDocument();
                if (
                    !menuLinkElem.classList.contains(
                        "site-nav__main-menu-item--mobile",
                    )
                ) {
                    document?.body.classList.remove(
                        "body-dropdown-open--desktop",
                    );
                    const layout = document?.querySelector(".layout--hidden");
                    if (layout) {
                        layout.classList.remove("layout--hidden");
                    }
                }
            }
            setModalIsOpen(false);
            setActiveMenuItem(null);
            setOpenedLink("");
        },
        [],
    );

    const onCloseSubNav = useCallback(() => {
        const parentElem = submenuParentElementRef.current;
        setSubmenuParentElement(null);
        setActiveMenuItem(null);

        if (parentElem && !isSSRRef.current) {
            setSafeTimeout(() => {
                parentElem.focus();
            }, 300);
        }
    }, [setSafeTimeout]);

    // SSR / pre-hydration: isMobileState is null — render nothing to avoid
    // desktop menu flash on mobile and hydration mismatches.
    // Early return before computing styles/classes that won't be used.
    if (isMobileState === null) {
        return null;
    }

    // Compute modal styles based on mobile vs desktop and iframe vs full page
    let contentPosition: CSSProperties["position"];
    let contentTop: CSSProperties["top"];
    let contentHeight: CSSProperties["height"];
    if (isMobileState) {
        contentPosition = "fixed";
        contentTop = "0px";
        contentHeight = iframeDocHeight ? `${iframeDocHeight}px` : "100%";
    } else {
        contentPosition = "relative";
        contentTop = `${(dropdownOffsetTop ?? 0) - 20}px`;
        contentHeight = "auto";
    }
    const modalStyle: ModalStyles = {
        overlay: {
            position: "absolute",
            backgroundColor: "rgba(255, 255, 255, 0)",
        },
        content: {
            position: contentPosition,
            top: contentTop,
            marginTop: "0",
            width: "100%",
            height: contentHeight,
            overflow: "hidden",
            border: "none",
            boxShadow: "0 10px 10px -5px rgba(0,0,0,.3)",
        },
    };

    const activeMenuLabel = activeMenuItem
        ? t`${activeMenuItem.value.title} Menu`
        : "";

    const dropdownContentClasses = classNames({
        "main-dropdown-menu__content": true,
        "main-dropdown-menu__content--closed": activeMenuItem,
        "l-capped-width": true,
    });

    const dropdownSubContentClasses = classNames({
        "main-dropdown-menu__sub-content": true,
        "main-dropdown-menu__sub-content--open": activeMenuItem,
        "l-capped-width": true,
    });

    // Get promo ads and nav footer
    const { promoAds, navFooter } = getPromoAdsAndFooter(activeMenuItem);

    // Fix CLS: remove getWindow() at render time — use CSS max() instead of JS ternary
    const closeBtnPropsDesktop = {
        style: {
            top: "1.5rem",
            right: "max(1.5rem, calc((100% - 1200px) / 2 - 20px))",
        },
    };

    const closeBtnPropsMobile = {
        style: {
            top: "0.5rem",
            right: "0.5rem",
        },
    };

    if (isMobileState) {
        return (
            <>
                <MobileTriggerButton onOpenModal={() => onOpenModal()} />
                <Modal
                    aria={{ modal: true }}
                    contentLabel={activeMenuLabel}
                    isOpen={modalIsOpen}
                    style={modalStyle}
                    onRequestClose={onCloseModal}
                    role="dialog"
                    onAfterOpen={() => {
                        focusElement(
                            ".main-dropdown-menu .main-dropdown-menu__close",
                        );
                    }}
                    closeBtnProps={closeBtnPropsMobile}
                >
                    <h1 className="ada-screenreader-only">{t`Shop Menu`}</h1>
                    <div className="main-dropdown-menu">
                        <CloseButton
                            className="main-dropdown-menu__close"
                            iconClassName="main-dropdown-menu__close-icon"
                            onClose={onCloseModal}
                        />
                        <div
                            id="main-menu"
                            className={dropdownContentClasses}
                            aria-expanded={!activeMenuItem}
                            aria-label={t`Mattresses, Bed Bases, Pillows, Bedding, and More From Tempur`}
                        >
                            <MenuList
                                menuItems={menuItems}
                                isMobile={isMobileState}
                                onOpenModal={onOpenModal}
                                openedLink={openedLink}
                            />
                        </div>
                        <div
                            className={dropdownSubContentClasses}
                            aria-expanded={!!activeMenuItem}
                            id="menu-subnav"
                        >
                            <BackButton onBack={onCloseSubNav} />
                            <DropdownContent
                                activeMenuItem={activeMenuItem}
                                isMobile={isMobileState}
                            />
                            <MenuAdverts promos={promoAds} />
                        </div>
                        {navFooter}
                    </div>
                </Modal>
            </>
        );
    }

    return (
        <>
            <MenuList
                menuItems={menuItems}
                isMobile={isMobileState}
                onOpenModal={onOpenModal}
                openedLink={openedLink}
            />
            <Modal
                aria={{ modal: true }}
                contentLabel={activeMenuLabel}
                isOpen={modalIsOpen}
                style={modalStyle}
                onRequestClose={onCloseModal}
                role="dialog"
                closeBtnProps={closeBtnPropsDesktop}
            >
                <h1 className="ada-screenreader-only">{t`Shop Menu`}</h1>
                <div className="main-dropdown-menu">
                    <div
                        id="main-menu"
                        className={dropdownContentClasses}
                        aria-expanded={!!activeMenuItem}
                        aria-label={t`Mattresses, Bed Bases, Pillows, Bedding, and More From Tempur`}
                    >
                        <DropdownContent
                            activeMenuItem={activeMenuItem}
                            isMobile={isMobileState}
                            hasAdverts={promoAds.length > 0}
                        />
                        <MenuAdverts promos={promoAds} key={promoAds.length} />
                    </div>
                    {navFooter}
                </div>
            </Modal>
        </>
    );
};
